
import { useState, useEffect } from 'react';
import { formatLocalDateTime } from '@/lib/date';
import { healthService } from '@/services/healthService';

const ApiStatus = () => {
  const [statuses, setStatuses] = useState<Array<{ name: string; status: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    const fetchServiceStatuses = async () => {
      try {
        setLoading(true);
        const serviceStatuses = await healthService.getServiceStatuses();
        setStatuses(serviceStatuses);
        setLastUpdated(new Date());
      } catch (error) {
        console.error('Error fetching service statuses:', error);
        // Fallback to basic operational status
        setStatuses([
          { name: 'API', status: 'operational' },
          { name: 'Dashboard', status: 'operational' },
          { name: 'Database', status: 'operational' },
          { name: 'Memory Storage', status: 'operational' }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceStatuses();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchServiceStatuses, 30000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-recallio-dark-gray rounded-lg border border-recallio-light-gray/50 overflow-hidden">
      <div className="p-5 border-b border-recallio-light-gray/50">
        <h3 className="font-medium">API Status</h3>
      </div>
      <div className="p-5 space-y-4">
        {loading ? (
          // Loading skeleton
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="flex items-center justify-between animate-pulse">
              <div className="h-4 bg-recallio-medium-gray rounded w-24"></div>
              <div className="flex items-center">
                <div className="h-2.5 w-2.5 rounded-full mr-2 bg-recallio-medium-gray"></div>
                <div className="h-4 bg-recallio-medium-gray rounded w-16"></div>
              </div>
            </div>
          ))
        ) : (
          statuses.map((item) => (
            <div key={item.name} className="flex items-center justify-between">
              <div className="font-medium">{item.name}</div>
              <div className="flex items-center">
                <div 
                  className={`h-2.5 w-2.5 rounded-full mr-2 ${
                    item.status === 'operational' 
                      ? 'bg-recallio-green'
                      : item.status === 'degraded' 
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                  }`}
                />
                <span className="text-sm">
                  {item.status === 'operational' 
                    ? 'Operational'
                    : item.status === 'degraded' 
                      ? 'Degraded'
                      : 'Outage'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="px-5 py-3 bg-recallio-medium-gray border-t border-recallio-light-gray/50 text-sm text-muted-foreground">
        Last updated: {formatLocalDateTime(lastUpdated)}
      </div>
    </div>
  );
};

export default ApiStatus;
